/**
 * Context for managing the report side panel state.
 *
 * Widgets (ReportWidget, FactoidWidget) call openReport() to push their
 * content into the side panel. The panel is rendered at the route level
 * via Chat.tsx's rightPanel prop.
 */

"use client"

import { createContext, useContext, useState, useCallback, useEffect, type PropsWithChildren } from "react"

/** The report data displayed in the side panel. */
export interface ReportPanelData {
  /** Discriminator: "report" for full reports, "factoid" for fact-checks. */
  kind: "report" | "factoid"
  /** Report or factoid title. */
  title: string
  /** Executive summary or answer summary. */
  summary: string
  /** The full report/answer body in markdown (used for "report" kind). */
  markdown: string
  /** Structured factoid sections (used for "factoid" kind). */
  sections?: { label: string; content: string }[]
}

/** Context value shape. */
interface ReportPanelContextValue {
  /** The currently loaded report, or null if panel is empty. */
  report: ReportPanelData | null
  /** Whether the side panel is open. */
  isOpen: boolean
  /** Open the panel with the given report data. */
  openReport: (data: ReportPanelData) => void
  /** Close the panel (data is retained so it can be reopened). */
  closeReport: () => void
}

const ReportPanelCtx = createContext<ReportPanelContextValue>({
  report: null,
  isOpen: false,
  openReport: () => {},
  closeReport: () => {},
})

/**
 * Hook for widgets to open/close the report side panel.
 *
 * @returns The report panel context value.
 */
export function useReportPanel(): ReportPanelContextValue {
  return useContext(ReportPanelCtx)
}

/**
 * Provider that manages report side panel state. Wrap the chat route in this.
 * Automatically closes the panel when sessionId changes (user switches chat).
 *
 * @param props.sessionId - The active session ID. Panel closes on change.
 */
export function ReportPanelProvider({ sessionId, children }: PropsWithChildren<{ sessionId: string }>) {
  const [report, setReport] = useState<ReportPanelData | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  // Close the panel and clear data when the session changes.
  useEffect(() => {
    setIsOpen(false)
    setReport(null)
  }, [sessionId])

  const openReport = useCallback((data: ReportPanelData) => {
    setReport(data)
    setIsOpen(true)
  }, [])

  const closeReport = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <ReportPanelCtx.Provider value={{ report, isOpen, openReport, closeReport }}>
      {children}
    </ReportPanelCtx.Provider>
  )
}
